import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getDefaultStore, useAtomValue } from "jotai";
import { authTokenAtom } from "./auth-state";
import type {
  WsEventEnvelope,
  WsServerMsg,
  WsSubscribeMsg,
  WsUnsubscribeMsg,
  WsPongMsg,
} from "@tubo/api-models/business/ws/ws.schemas";

// ==================== Re-exports ====================

export type { WsEventEnvelope };

export type WsOutgoingMessage = WsSubscribeMsg | WsUnsubscribeMsg | WsPongMsg;

export type WsConnectionState = "connected" | "connecting" | "disconnected";

export type SubscribeStatus = "pending" | "subscribed" | "denied";

export type EventHandler = (event: WsEventEnvelope) => void;

export interface SubOpts {
  resType: string;
  resId: string;
  handler: EventHandler;
}

export interface SubState {
  status: SubscribeStatus;
}

// ==================== Constants ====================

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const RECONNECT_MAX_ATTEMPTS = 10;
const PROBE_INTERVAL_MS = 60000;
const STABLE_CONNECTION_MS = 30000;
const EVT_ID_LRU_SIZE = 500;

// ==================== Context ====================

export interface WebSocketContextValue {
  connected: boolean;
  connectionState: WsConnectionState;
  subscribe: (opts: SubOpts) => void;
  unsubscribe: (opts: SubOpts) => void;
  hasEventOccurred: (eventType: string) => boolean;
  send: (msg: WsOutgoingMessage) => void;
}

const noop = () => { /* no-op */ };
const noopSubscribe = (_opts: SubOpts) => { /* no-op */ };
const noopSend = (_msg: WsOutgoingMessage) => { /* no-op */ };

const WebSocketContext = createContext<WebSocketContextValue>({
  connected: false,
  connectionState: "disconnected",
  subscribe: noopSubscribe,
  unsubscribe: noopSubscribe,
  hasEventOccurred: () => false,
  send: noopSend,
});

export function useWebSocket(): WebSocketContextValue {
  return useContext(WebSocketContext);
}

// ==================== Helpers ====================

function buildWsUrl(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/intl/ws?token=${encodeURIComponent(token)}`;
}

function getToken(): string | null {
  try {
    return getDefaultStore().get(authTokenAtom);
  } catch {
    return null;
  }
}

// ==================== LRU Event ID Set ====================

function createLruSet(maxSize: number) {
  const set = new Set<string>();
  return {
    has(id: string): boolean {
      if (!set.has(id)) return false;
      set.delete(id);
      set.add(id);
      return true;
    },
    add(id: string): void {
      if (set.has(id)) {
        set.delete(id);
      }
      set.add(id);
      while (set.size > maxSize) {
        const first = set.values().next().value;
        if (first !== undefined) set.delete(first);
      }
    },
  };
}

// ==================== Subscription Manager ====================

interface SubEntry {
  resType: string;
  resId: string;
  handlers: Set<EventHandler>;
  count: number;
  status: SubscribeStatus;
}

function createSubscriptionManager() {
  const subs = new Map<string, SubEntry>();
  const denied = new Set<string>();

  function keyOf(resType: string, resId: string): string {
    return `${resType}:${resId}`;
  }

  function sendSubscribe(
    getWs: () => WebSocket | null,
    resType: string,
    resId: string,
  ): void {
    const ws = getWs();
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "subscribe", resType, resId }));
    }
  }

  function sendUnsubscribe(
    getWs: () => WebSocket | null,
    resType: string,
    resId: string,
  ): void {
    const ws = getWs();
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "unsubscribe", resType, resId }));
    }
  }

  function subscribe(opts: SubOpts, getWs: () => WebSocket | null): void {
    const { resType, resId, handler } = opts;
    const key = keyOf(resType, resId);
    let entry = subs.get(key);
    if (!entry) {
      entry = { resType, resId, handlers: new Set(), count: 0, status: "pending" };
      subs.set(key, entry);
    }
    entry.handlers.add(handler);
    entry.count += 1;
    if (entry.status === "pending" && !denied.has(key)) {
      sendSubscribe(getWs, resType, resId);
    }
  }

  function unsubscribe(opts: SubOpts, getWs: () => WebSocket | null): void {
    const { resType, resId, handler } = opts;
    const key = keyOf(resType, resId);
    const entry = subs.get(key);
    if (!entry) return;
    entry.handlers.delete(handler);
    entry.count -= 1;
    if (entry.count <= 0) {
      if (entry.status !== "denied") {
        sendUnsubscribe(getWs, resType, resId);
      }
      subs.delete(key);
    }
  }

  function handleSubscribed(resType: string, resId: string): void {
    const entry = subs.get(keyOf(resType, resId));
    if (entry) entry.status = "subscribed";
  }

  function handleDenied(resType: string, resId: string): void {
    const key = keyOf(resType, resId);
    denied.add(key);
    const entry = subs.get(key);
    if (entry) entry.status = "denied";
  }

  function dispatchEvent(event: WsEventEnvelope): void {
    const specificKey = keyOf(event.resType, event.resId);
    const wildcardKey = keyOf(event.resType, "*");

    // Dispatch to specific subscribers
    if (!denied.has(specificKey)) {
      const entry = subs.get(specificKey);
      if (entry) entry.status = "subscribed";
      entry?.handlers.forEach((h) => {
        try {
          h(event);
        } catch (err) {
          console.warn("[WS] Event handler error:", err);
        }
      });
    }

    // Dispatch to wildcard subscribers (resId="*")
    if (!denied.has(wildcardKey)) {
      const wildcardEntry = subs.get(wildcardKey);
      if (wildcardEntry) wildcardEntry.status = "subscribed";
      wildcardEntry?.handlers.forEach((h) => {
        try {
          h(event);
        } catch (err) {
          console.warn("[WS] Wildcard handler error:", err);
        }
      });
    }

    // Dispatch to channel subscribers（事件实际到达的容器频道——worker 按 parent 路由发布，
    // prjRun 事件只出现在 project:{projectId} 频道；项目级统一订阅如失效桥/run 流依赖此派发）
    if (event.channel) {
      const channelKey = keyOf(event.channel.resType, event.channel.resId);
      if (channelKey !== specificKey && !denied.has(channelKey)) {
        const channelEntry = subs.get(channelKey);
        if (channelEntry) channelEntry.status = "subscribed";
        channelEntry?.handlers.forEach((h) => {
          try {
            h(event);
          } catch (err) {
            console.warn("[WS] Channel handler error:", err);
          }
        });
      }
    }
  }

  function resendAll(getWs: () => WebSocket | null): void {
    for (const [key, entry] of subs) {
      if (denied.has(key)) continue;
      if (entry.count > 0) {
        entry.status = "pending";
        sendSubscribe(getWs, entry.resType, entry.resId);
      }
    }
  }

  /** 清空拒绝记录（切换租户重连时调用：旧租户的 deny 不应压制新租户的订阅） */
  function resetDenied(): void {
    denied.clear();
  }

  return {
    subscribe,
    unsubscribe,
    handleSubscribed,
    handleDenied,
    dispatchEvent,
    resendAll,
    resetDenied,
  };
}

type SubManager = ReturnType<typeof createSubscriptionManager>;

// ==================== Hooks ====================

function useWebSocketEventBus(subMgr: SubManager) {
  const occurredEventsRef = useRef<Set<string>>(new Set());

  const dispatchEvent = useCallback(
    (event: WsEventEnvelope) => {
      const key = `${event.resType}:${event.action}`;
      occurredEventsRef.current.add(key);
      subMgr.dispatchEvent(event);
    },
    [subMgr],
  );

  const hasEventOccurred = useCallback(
    (eventType: string) => occurredEventsRef.current.has(eventType),
    [],
  );

  return { dispatchEvent, hasEventOccurred };
}

function useWebSocketConnection(
  dispatchEvent: (event: WsEventEnvelope) => void,
  subMgr: SubManager,
): { connectionState: WsConnectionState; send: (msg: WsOutgoingMessage) => void; getWs: () => WebSocket | null } {
  const token = useAtomValue(authTokenAtom);
  const [connectionState, setConnectionState] = useState<WsConnectionState>("disconnected");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const openedAtRef = useRef<number | null>(null);
  const evtIdSetRef = useRef(createLruSet(EVT_ID_LRU_SIZE));
  /** 当前连接所用的 token：识别「token 变了但旧连接还活着」的租户切换场景 */
  const connTokenRef = useRef<string | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connectRef = useRef<() => void>(noop);

  connectRef.current = () => {
    if (!mountedRef.current) return;
    const currentToken = getToken();
    if (!currentToken) {
      setConnectionState("disconnected");
      return;
    }
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return;

    setConnectionState("connecting");
    const ws = (wsRef.current = new WebSocket(buildWsUrl(currentToken)));
    connTokenRef.current = currentToken;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setConnectionState("connected");
      openedAtRef.current = Date.now();
      subMgr.resendAll(() => wsRef.current);
    };

    ws.onmessage = (ev) => {
      try {
        const msg: WsServerMsg = JSON.parse(ev.data);

        if (msg.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (msg.type === "connected") return;
        if (msg.type === "subscribed") {
          subMgr.handleSubscribed(msg.resType, msg.resId);
          return;
        }
        if (msg.type === "denied") {
          subMgr.handleDenied(msg.resType, msg.resId);
          return;
        }
        if (msg.type === "evt") {
          if (evtIdSetRef.current.has(msg.evtId)) return;
          evtIdSetRef.current.add(msg.evtId);
          dispatchEvent(msg);
        }
      } catch (err) {
        console.warn("[WS] Message parse error:", err);
      }
    };

    ws.onclose = (ev) => {
      if (wsRef.current !== ws) return;
      if (!mountedRef.current) return;
      clearTimers();
      wsRef.current = null;
      const livedMs = openedAtRef.current !== null ? Date.now() - openedAtRef.current : 0;
      openedAtRef.current = null;
      setConnectionState("disconnected");
      if (ev.code === 1000 || ev.code === 4001 || ev.code === 4029) return;
      if (livedMs >= STABLE_CONNECTION_MS) reconnectAttemptRef.current = 0;
      const scheduleReconnect = (delay: number) => {
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connectRef.current();
        }, delay);
      };
      if (ev.code === 1001) {
        scheduleReconnect(0);
        return;
      }
      if (reconnectAttemptRef.current >= RECONNECT_MAX_ATTEMPTS) {
        scheduleReconnect(PROBE_INTERVAL_MS);
        return;
      }
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttemptRef.current, RECONNECT_MAX_MS);
      reconnectAttemptRef.current += 1;
      scheduleReconnect(delay);
    };

    ws.onerror = () => {
      // Error details available via close event
    };
  };

  // Mount / unmount
  useEffect(() => {
    mountedRef.current = true;
    connectRef.current();
    return () => {
      mountedRef.current = false;
      clearTimers();
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, "Component unmounted");
        }
        wsRef.current = null;
      }
    };
  }, [clearTimers]);

  // Connect when token available; disconnect on logout;
  // token 变化（切换租户/重新登录）且旧连接还活着时，必须拆掉旧连接重连——
  // 否则 WS 仍以旧租户 JWT 挂在线上，旧租户事件会持续触发新缓存失效。
  useEffect(() => {
    if (token) {
      if (connTokenRef.current !== token) {
        const ws = wsRef.current;
        if (ws) {
          // 摘掉旧连接回调，避免 close 事件触发旧的重连调度
          ws.onclose = null;
          ws.onmessage = null;
          wsRef.current = null;
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close(4001, "Tenant switched");
          }
        }
        // 旧租户的 deny 记录不得压制新租户订阅；事件去重集合同理
        subMgr.resetDenied();
        evtIdSetRef.current = createLruSet(EVT_ID_LRU_SIZE);
        reconnectAttemptRef.current = 0;
      }
      connectRef.current();
    } else {
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, "Logged out");
      }
    }
  }, [token, subMgr]);

  const send = useCallback((msg: WsOutgoingMessage) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const getWs = useCallback(() => wsRef.current, []);

  return { connectionState, send, getWs };
}

// ==================== Provider ====================

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const subMgrRef = useRef(createSubscriptionManager());
  const subMgr = subMgrRef.current;

  const { dispatchEvent, hasEventOccurred } = useWebSocketEventBus(subMgr);
  const { connectionState, send, getWs } = useWebSocketConnection(dispatchEvent, subMgr);

  const subscribe = useCallback(
    (opts: SubOpts) => subMgr.subscribe(opts, getWs),
    [subMgr, getWs],
  );

  const unsubscribe = useCallback(
    (opts: SubOpts) => subMgr.unsubscribe(opts, getWs),
    [subMgr, getWs],
  );

  return (
    <WebSocketContext.Provider
      value={{
        connected: connectionState === "connected",
        connectionState,
        subscribe,
        unsubscribe,
        hasEventOccurred,
        send,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}
